import * as React from 'react'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  findAcceptedTypeOptionForBlock,
  getAllPossibleTypeOptions,
} from '@basehub/data/replicache/schema/block/reference/util'
import {
  Block,
  ReferenceBlock,
  RichTextBlock,
  isRootBlock,
} from '@basehub/data/replicache/index'
import { TreeNode } from '@basehub/utils/tree/index'

export const usePossibleReferences = (opts?: { block: Block }) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getPath = useWorkingTreeStore((s) => s.tree?.getPath)
  const referenceableSet = useWorkingTreeStore(
    (s) => s.tree?.compoundIndexes.get('referenceable') ?? new Set<string>()
  )
  const componentsSet = useWorkingTreeStore(
    (s) => s.tree?.compoundIndexes.get('components') ?? new Set<string>()
  )
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)

  const typeOptions = React.useMemo(() => {
    if (!getNode || !componentsSet || !getChildrenKeys) return []
    return getAllPossibleTypeOptions(
      { getNode, componentsSet, getChildrenKeys },
      opts?.block
    )
  }, [componentsSet, getChildrenKeys, getNode, opts?.block])

  const getPossibleReferences = React.useCallback(
    (block: ReferenceBlock | RichTextBlock) => {
      if (!getNode || !getPath) return []

      // Optimization: Create a Set for faster lookups
      const richTextActiveInstancesMap = new Map<string, Set<string>>()

      const filteredNodes: Array<TreeNode<Block>> = []

      // Single pass through referenceable nodes
      for (const id of referenceableSet) {
        const node = getNode(id)
        if (!node) continue
        const nodeBlock = node.value

        // Early exit conditions
        if (isRootBlock(nodeBlock) || nodeBlock.id === block.id) continue

        // Type option check
        if (
          findAcceptedTypeOptionForBlock({
            refBlock: block,
            evaluatedBlock: nodeBlock,
            typeOptions,
          })
        ) {
          filteredNodes.push(node)
        }
      }

      // Filter nodes based on path and active instances
      const result = filteredNodes.filter((node) => {
        // Get/cache path
        const path: string[] | null =
          getPath(node.key, { format: 'array' }) ?? null
        if (!path) return false

        // Check rich text ancestors
        const richTextAncestorId = path.find((ancestorId) =>
          richTextActiveInstancesMap.has(ancestorId)
        )

        if (richTextAncestorId) {
          const activeInstances =
            richTextActiveInstancesMap.get(richTextAncestorId)
          return path.some((id) => activeInstances?.has(id))
        }

        return true
      })

      return result
    },
    [getNode, getPath, referenceableSet, typeOptions]
  )

  return { typeOptions, getPossibleReferences }
}
